import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CTASection from './components/CTASection';
import CategoryGrid from './components/CategoryGrid';
import BenefitsSection from './components/BenefitsSection';
import ComparisonSection from './components/ComparisonSection';
import SEOPreview from './components/SEOPreview';
import SEOArticlesSection from './components/SEOArticlesSection';
import TestimonialSection from './components/TestimonialSection';
import FAQSection from './components/FAQSection';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.click';

export default function Home() {
  return (
    <main>
      {/* WebSite Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'LitBuy Spreadsheet Hub',
            url: siteUrl,
            description: 'The ultimate resource for litbuy spreadsheet guides, templates, comparisons, and best practices for organized fashion shopping.',
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: `${siteUrl}/?q={search_term_string}`,
              },
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'LitBuy Spreadsheet Hub',
            url: siteUrl,
            description: 'Comprehensive resource center for mastering spreadsheet-based buying management.',
            sameAs: [
              siteUrl,
            ],
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
            ],
          }),
        }}
      />

      <Navbar />
      <HeroSection />
      <CTASection />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
            Your Go-To LitBuy Spreadsheet Resource
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            This website is the ultimate resource hub for anyone looking to master the 
            <strong> litbuy spreadsheet</strong> workflow. Whether you&rsquo;re a beginner exploring what 
            a litbuy spreadsheet can do for you, or an experienced user seeking advanced tips and 
            templates, we have everything covered. Our guides, comparison charts, and FAQs are 
            crafted to help you make smarter buying decisions while saving time and staying organized.
          </p>
          <p className="mt-3 text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Looking to put your knowledge into action? Browse our curated fashion categories below 
            and visit the <strong>Kaspreadsheet store</strong> to shop top-quality clothing, shoes, 
            accessories, and more at competitive prices.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 bg-primary-500 text-background-50 rounded-full font-label font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              litbuy spreadsheet
              <span className="w-4 h-4 inline-flex items-center justify-center ml-2">
                <i className="ri-arrow-right-line"></i>
              </span>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 border-2 border-foreground-300 text-foreground-700 rounded-full font-label font-semibold text-sm hover:border-primary-500 hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>
        </div>
      </section>

      <CategoryGrid />
      <BenefitsSection />
      <ComparisonSection />
      <SEOPreview />
      <SEOArticlesSection />
      <TestimonialSection />
      <FAQSection />

      <section className="py-16 md:py-20 bg-primary-500">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
            Ready to Start Shopping?
          </h2>
          <p className="mt-4 text-sm md:text-base text-background-50/80 max-w-xl mx-auto leading-relaxed">
            Visit Kaspreadsheet today and explore a world of fashion, accessories, 
            and more. Your next favorite find is just a click away.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 bg-background-50 text-primary-600 rounded-full font-label font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
            >
              litbuy spreadsheet
              <span className="w-4 h-4 inline-flex items-center justify-center ml-2">
                <i className="ri-arrow-right-line"></i>
              </span>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-7 py-3 border-2 border-background-50/70 text-background-50 rounded-full font-label font-semibold text-sm hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}